import React, { useMemo } from "react";
import { Card, CardBody, CardHeader, Table, Row, Col, Spinner, Button } from "react-bootstrap";
import Chart from "react-google-charts";
import { useLocation, useNavigate } from "react-router-dom";
import BreadCrumbs from "../../../../../../components/BreadCrumbs";
import { useFirebaseCartSummaries } from "../../../../../../hooks/useFirebaseCartSummaries";
import { useSelector } from "react-redux";

interface ProductDetail {
  id: string;
  productId: string;
  displayName: string;
  totalAvailable: number;
  totalReserved: number;
  totalSold: number;
  totalAllocated: number;
  totalReceived: number;
  channels: {
    [channelName: string]: {
      available: number;
      reserved: number;
      sold: number;
      allocated: number;
    };
  };
}

const InventoryItemView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { product, selectedDate, cartId, cartName, restaurantId: stateRestaurantId } = location.state || {};

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.id,
  ) || stateRestaurantId;

  // Use the same cart summaries hook
  const {
    data: cartSummaries,
    loading,
    error
  } = useFirebaseCartSummaries(restaurantId, new Date(selectedDate));

  // Get current cart
  const currentCart = useMemo(() => {
    if (!cartSummaries || !cartId) return null;
    return cartSummaries.find(cart => cart.id === cartId);
  }, [cartSummaries, cartId]);

  // Get product details from cart summaries
  const productDetails = useMemo<ProductDetail | null>(() => {
    if (!cartSummaries || !product) return null;

    let productData: ProductDetail | null = null;

    // Find the product in all carts or specific cart
    const cartsToSearch = cartId 
      ? cartSummaries.filter(cart => cart.id === cartId)
      : cartSummaries;

    cartsToSearch.forEach(cart => {
      const foundProduct = Object.values(cart.products).find(p => p.id === product.id);
      
      if (foundProduct && !productData) {
        let totalAvailable = 0;
        let totalReserved = 0;
        let totalSold = 0;
        let totalAllocated = 0;
        let totalReceived = 0;
        const channels: any = {};

        Object.entries(foundProduct.channels).forEach(([channelName, channel]) => {
          totalAvailable += channel.available || 0;
          totalReserved += channel.reserved || 0;
          totalSold += channel.sold || 0;
          totalAllocated += channel.allocated || 0;
          totalReceived += (channel.available || 0) + (channel.sold || 0) + (channel.reserved || 0);

          channels[channelName] = {
            available: channel.available || 0,
            reserved: channel.reserved || 0,
            sold: channel.sold || 0,
            allocated: channel.allocated || 0
          };
        });

        productData = {
          id: foundProduct.id,
          productId: foundProduct.productId,
          displayName: foundProduct.displayName,
          totalAvailable,
          totalReserved,
          totalSold,
          totalAllocated,
          totalReceived,
          channels
        };
      }
    });

    return productData;
  }, [cartSummaries, product, cartId]);

  // Channel chart data - ONLY Available and Sold
  const channelChartData = useMemo(() => {
    if (!productDetails) return [];

    const data: any[] = [["Channel", "Available", "Sold"]];
    
    Object.entries(productDetails.channels).forEach(([channelName, channel]) => {
      if (channel.available > 0 || channel.sold > 0) {
        data.push([channelName, channel.available, channel.sold]);
      }
    });

    return data.length > 1 ? data : [];
  }, [productDetails]);

  // Pie chart data
  const pieChartData = useMemo(() => {
    if (!productDetails) return [];

    return [
      ["Status", "Quantity"],
      ["Available", productDetails.totalAvailable],
      ["Sold", productDetails.totalSold],
      ["Reserved", productDetails.totalReserved],
    ];
  }, [productDetails]);

  // Back button handler - Navigate to InventoryView with cart data
  const handleBack = () => {
    navigate("/carts/carts-list/view/inventory-view", {
      state: {
        cartId: cartId,
        cartName: cartName || currentCart?.cartName || "",
        restaurantId: restaurantId,
        selectedDate: selectedDate
      }
    });
  };

  if (loading) {
    return (
      <>
        <BreadCrumbs hasSubMenus={false} />
        <Card className="mt-3">
          <CardBody className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading product details...</p>
          </CardBody>
        </Card>
      </>
    );
  }

  if (error || !productDetails) {
    return (
      <>
        <BreadCrumbs hasSubMenus={false} />
        <Card className="mt-3">
          <CardBody>
            <div className="alert alert-danger">
              <h5>Error Loading Product</h5>
              <p>{error?.message || "Product not found"}</p>
              <button className="btn btn-outline-primary" onClick={handleBack}>
                Go Back
              </button>
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      
      {/* Back Button */}
      <div className="mb-3 mt-3">
        <Button 
          variant="outline-secondary" 
          onClick={handleBack}
          className="d-flex align-items-center gap-2"
        >
         Back to Cart Inventory
        </Button>
      </div>

      {/* Product Header */}
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h3 className="mb-2">{productDetails.displayName}</h3>
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-1">
                    <strong>Product ID:</strong> {productDetails.productId}
                  </p>
                  <p className="mb-1">
                    <strong>Date:</strong> {selectedDate}
                  </p>
                  <p className="mb-1">
                    <strong>Cart:</strong> {cartName || currentCart?.cartName || cartId}
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="mb-1">
                    <strong>Total Received:</strong> {productDetails.totalReceived}
                  </p>
                  <p className="mb-1">
                    <strong>Available Stock:</strong> {productDetails.totalAvailable}
                  </p>
                  <p className="mb-1">
                    <strong>Reserved:</strong> {productDetails.totalReserved}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Channel Chart - Only shows Available and Sold */}
      <Card className="mt-3">
        <CardHeader>
          <h5 className="mb-0">Channel-wise Available vs Sold</h5>
        </CardHeader>
        <CardBody>
          {channelChartData.length > 1 ? (
            <Chart
              chartType="ColumnChart"
              width="100%"
              height="400px"
              data={channelChartData}
              options={{
                chartArea: { width: "70%", height: "70%" },
                isStacked: false,
                hAxis: {
                  title: "Channels",
                  slantedText: true,
                  slantedTextAngle: 45,
                },
                vAxis: {
                  title: "Quantity",
                  minValue: 0,
                },
                series: {
                  0: { color: "#28a745" },
                  1: { color: "#007bff" },
                },
                legend: { position: "top" },
              }}
            />
          ) : (
            <p className="text-center text-muted py-4">No channel data available</p>
          )}
        </CardBody>
      </Card>

      {/* Pie Chart and Channel Details */}
      <Row className="mt-3">
        <Col lg={5}>
          <Card className="h-100">
            <CardHeader>
              <h5 className="mb-0">Distribution</h5>
            </CardHeader>
            <CardBody>
              <Chart
                chartType="PieChart"
                width="100%"
                height="300px"
                data={pieChartData}
                options={{
                  pieHole: 0.4,
                  slices: {
                    0: { color: "#28a745" },
                    1: { color: "#007bff" },
                    2: { color: "#ffc107" },
                  },
                  legend: { position: "labeled" },
                }}
              />
            </CardBody>
          </Card>
        </Col>
        <Col lg={7}>
          <Card className="h-100">
            <CardHeader>
              <h5 className="mb-0">Channel Details</h5>
            </CardHeader>
            <CardBody>
              <div className="table-responsive">
                <Table bordered hover>
                  <thead className="table-light">
                    <tr>
                      <th>Channel</th>
                      <th className="text-center">Available</th>
                      <th className="text-center">Reserved</th>
                      <th className="text-center">Sold</th>
                      <th className="text-center">Allocated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(productDetails.channels).map(([channelName, channel]) => (
                      <tr key={channelName}>
                        <td className="fw-medium">{channelName}</td>
                        <td className="text-center">
                          <span className={channel.available <= 0 ? "text-danger fw-bold" : "text-success fw-bold"}>
                            {channel.available}
                          </span>
                        </td>
                        <td className="text-center text-warning fw-medium">{channel.reserved}</td>
                        <td className="text-center text-primary fw-medium">{channel.sold}</td>
                        <td className="text-center text-info fw-medium">{channel.allocated}</td>
                      </tr>
                    ))}
                    <tr className="table-light">
                      <td className="fw-bold">Total</td>
                      <td className="text-center fw-bold">{productDetails.totalAvailable}</td>
                      <td className="text-center fw-bold">{productDetails.totalReserved}</td>
                      <td className="text-center fw-bold">{productDetails.totalSold}</td>
                      <td className="text-center fw-bold">{productDetails.totalAllocated}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default InventoryItemView;